import { Title } from "../../Components/Title"
import { Paragraph } from "../../Components/Paragraph"
import Button from "../../Components/Button"
import React, { useEffect, useMemo } from "react"
import { LOGO } from "../../constants/textConstants"
import { useNavigate } from "react-router-dom"
import ProgressIndicator from "../../Components/MultiStepFlow/ProgressIndicator"
import { getPlanStudentFirstName } from "../../lib/plan"
import { useOutletContextPlan } from "../../hooks/useOutletContextPlan"
import { PLAN_STATUS } from "../../constants/planStatus"
import { PLAN_STATUS_LABELS } from "../../constants/planStatusBadges"
import { INVITE_STATUS } from "../../constants/inviteStatus"
import { usePlanRoles } from "../../hooks/usePlanRoles"
import Spinner from "../../Components/Spinner"
import { useAtom } from "jotai"
import { channelAtom, planTokenAtom, userAtom } from "../../state/"

export const SetupChecklist = () => {
  const { plan, pendingGuide, guide, getPlan, refreshPlans, student } = useOutletContextPlan()
  const { isInitiator, isGuide } = usePlanRoles()
  const [channel] = useAtom(channelAtom)
  const [planToken] = useAtom(planTokenAtom)
  const [user] = useAtom(userAtom)

  const navigate = useNavigate()
  const planName = plan?.name || "Your Plan"
  const studentName = getPlanStudentFirstName(plan)

  useEffect(() => {
    Promise.all([getPlan(), refreshPlans()])
  }, [getPlan, refreshPlans])

  useEffect(() => {
    if (channel && planToken && user) {
      const handlePlanUpdate = (data) => {
        if (data.user_id != user.id) {
          Promise.all([getPlan(), refreshPlans()])
        }
      }

      channel.bind("plan." + planToken, handlePlanUpdate)

      return () => {
        channel.unbind("plan." + planToken, handlePlanUpdate)
      }
    }
  }, [channel, planToken, user, getPlan, refreshPlans])

  const awaitingStudentAcceptance = useMemo(
    () =>
      (plan?.status === PLAN_STATUS.INVITE_STUDENT ||
        plan?.status === PLAN_STATUS.SIT_WITH_STUDENT) &&
      student?.invitation_status === INVITE_STATUS.PENDING,
    [plan, student],
  )

  const studentAccepted = student?.invitation_status === INVITE_STATUS.ACCEPTED

  const awaitingGuideAcceptance = useMemo(
    () => plan?.status === PLAN_STATUS.CHOOSE_GUIDE && pendingGuide,
    [plan, pendingGuide],
  )

  const guideAccepted = guide?.invitation_status === INVITE_STATUS.ACCEPTED

  const mentorsInvited =
    plan?.status === PLAN_STATUS.SEND_PLAN ||
    plan?.status === PLAN_STATUS.PLAN_COMPLETE ||
    plan?.status === PLAN_STATUS.CHECKLIST_COMPLETE

  const planComplete = plan?.completed

  const currentStep = useMemo(
    () =>
      plan?.status === PLAN_STATUS.CHOOSE_GUIDE
        ? guideAccepted
          ? "guide-invited"
          : mentorsInvited
            ? planComplete
              ? "plan-complete"
              : "complete-plan"
            : plan?.status
        : plan?.status || "choose-guide",
    [plan, guideAccepted, mentorsInvited, planComplete],
  )

  const steps = useMemo(
    () => [
      {
        id: guideAccepted ? "guide-invited" : "choose-guide",
        title: PLAN_STATUS_LABELS[PLAN_STATUS.CHOOSE_GUIDE],
      },
      {
        id: awaitingStudentAcceptance || studentAccepted ? "student-invited" : "invite-student",
        title: PLAN_STATUS_LABELS[PLAN_STATUS.INVITE_STUDENT],
      },
      {
        id: "sit-with-student",
        title: `Complete ${PLAN_STATUS_LABELS[PLAN_STATUS.SIT_WITH_STUDENT]}`,
      },
      {
        id: mentorsInvited ? "mentors-invited" : "invite-mentors",
        title: PLAN_STATUS_LABELS[PLAN_STATUS.INVITE_MENTORS],
      },
      {
        id: "plan-complete",
        title: `Complete ${PLAN_STATUS_LABELS[PLAN_STATUS.SEND_PLAN]}`,
      },
    ],
    [awaitingStudentAcceptance, mentorsInvited, guideAccepted],
  )

  const currentStepIndex =
    currentStep === PLAN_STATUS.CHOOSE_GUIDE && guideAccepted
      ? 0 : currentStep === PLAN_STATUS.SEND_PLAN && !planComplete ? steps.length - 1
      : steps.findIndex(step => currentStep === step.id)
  const activeStepIndex = currentStepIndex === -1 ? steps.length : currentStepIndex
  const completedSteps = steps.filter((_, index) => index < activeStepIndex).map(step => step.id)

  const showProgress = completedSteps.length > 0

  const userCanProceed = useMemo(
    () => (currentStep === PLAN_STATUS.CHOOSE_GUIDE ? isInitiator : isGuide),
    [currentStep, isInitiator, isGuide],
  )

  const handleStepClick = (stepId, index) => {
    if (index === steps.length - 1) {
      navigate(
        planComplete
          ? `/plan/${plan.token}/checklist/plan-complete`
          : `/plan/${plan.token}/checklist/complete-mentor-activities`,
      )
      return
    }
    if (index === activeStepIndex) {
      if (stepId === "choose-guide" && awaitingGuideAcceptance) {
        navigate(`/plan/${plan.token}/checklist/guide-invited`)
        return
      }
      navigate(`/plan/${plan.token}/checklist/${stepId}`)
    } else if (completedSteps.includes(stepId)) {
      if (stepId === "choose-guide") {
        return
      }
      navigate(`/plan/${plan.token}/checklist/${stepId}`)
    }
  }


  const getButtonVariant = index => {
    if (awaitingGuideAcceptance && steps[index].id === "choose-guide") {
      return "waiting"
    } else if (index === steps.length - 1 && activeStepIndex >= index) {
      return planComplete ? "success" : "secondary"
    } else if (completedSteps.includes(steps[index].id)) {
      return "success"
    } else if (index === activeStepIndex) {
      return "secondary"
    } else {
      return "locked"
    }
  }

  if (!plan) {
    return (
      <div className={"flex h-full w-full flex-col items-center justify-center"}>
        <Spinner size={"xl"} />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="bg-background px-4 py-12">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-3 items-center gap-4">
            <div className="w-24"></div>
            <div className="flex-shrink-0 justify-self-center">
              <img src={LOGO} alt="Logo" className="h-8" />
            </div>
            <div className="flex w-[15vw] flex-shrink-0 justify-end justify-self-end sm:w-[25vw] md:w-64">
              {showProgress && (
                <ProgressIndicator
                  currentStep={activeStepIndex}
                  totalSteps={steps.length}
                  showStepText={currentStep !== "checklist-complete"}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-grow flex-col items-center justify-start gap-6 pb-12">
        <div className="text-4xl">{planComplete ? "🎉🎉🎉🎉🎉" : "✏️"}</div>
        <Title>{planComplete ? "Congratulations!" : "Set Up Checklist"}</Title>
        {planComplete ? (
          <Paragraph className="max-w-72 text-center">
            <span className={"font-bold"}>{planName}</span> is complete!
          </Paragraph>
        ) : (
          <Paragraph className="max-w-72 text-center">
            Follow the instructions below to setup <span className={"font-bold"}>{planName}</span>.
          </Paragraph>
        )}
        <div className="flex flex-col gap-4">
          {steps.map((step, index) => (
            <Button
              id={step.id}
              key={step.id}
              disabled={index > activeStepIndex || !userCanProceed}
              width="w-80"
              variant={getButtonVariant(index)}
              onClick={() => handleStepClick(step.id, index)}
            >
              {step.title}
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}
